import { isBannerLive, type BannerScheduleWindow } from './bannerSchedule';

/**
 * 배너 목록의 범위(탭)·정렬 선택을 순수 함수로 분리한 것.
 *
 * 컴포넌트에 인라인으로 두면 null 처리와 동점 처리(아래)가 테스트 없이 굳는다.
 */

/** 목록 범위 탭. */
export type BannerScope =
  /** 지금 실제로 스토어프론트에 노출 중인 것만(활성 + 예약 윈도우 안). */
  | 'live'
  /** 비활성·기간 종료·노출 예정까지 전부. */
  | 'all';

/**
 * 정렬 기준.
 *
 * ★`sortOrder` 만 드래그 정렬과 함께 쓸 수 있다. BE reorder 는 전달한 배열의 인덱스를 그대로
 * sort_order 로 저장하고 **누락된 배너는 건드리지 않으므로**, 부분집합이나 다른 정렬 상태에서
 * 드래그하면 화면에 없는 배너의 기존 순서와 값이 충돌한다({@link canDragToReorder}).
 */
export type BannerSortKey = 'sortOrder' | 'startAt' | 'endAt' | 'updated';

export interface BannerListRow extends BannerScheduleWindow {
  sortOrder: number;
  isActive: boolean;
  updatedAt?: string | null;
}

/** 드래그로 순서를 바꿔도 안전한 조합인지. 전체 범위 + 노출 순서 정렬일 때만 참. */
export const canDragToReorder = (scope: BannerScope, sortKey: BannerSortKey): boolean =>
  scope === 'all' && sortKey === 'sortOrder';

const timeOf = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * 범위 필터 + 정렬을 적용한 목록을 돌려준다(입력 배열은 변경하지 않는다).
 *
 * - `sortOrder`: 스토어프론트 노출 순서 그대로.
 * - `startAt`: 시작일 오름차순 — "다음에 무엇이 걸리나"(노출 예정)를 본다.
 * - `endAt`: 종료일 오름차순 — "무엇이 곧 내려가나"를 본다.
 * - `updated`: 최근 수정 내림차순. 값이 없으면 가장 오래된 것으로 취급해 맨 뒤.
 *
 * ★null 의 의미를 한 규칙으로 통일한다: 시작 없음 = "태초부터 시작"(맨 앞), 종료 없음 =
 * "끝나지 않음"(맨 뒤). 그래서 상시 노출 배너는 시작일 순에서 위로, 종료일 순에서 아래로 간다 —
 * 두 정렬 모두 "임박한 변화"를 찾는 용도라 이 방향이라야 쓸모가 있다.
 *
 * 동점은 모두 `sortOrder` 로 깨서 렌더 순서가 흔들리지 않게 한다(정렬 안정성).
 */
export const selectBannerRows = <T extends BannerListRow>(
  rows: ReadonlyArray<T>,
  scope: BannerScope,
  sortKey: BannerSortKey,
  now: number = Date.now(),
): T[] => {
  const scoped = scope === 'live' ? rows.filter((r) => isBannerLive(r, now)) : rows.slice();
  const sorted = scoped.slice();

  if (sortKey === 'sortOrder') {
    sorted.sort((a, b) => a.sortOrder - b.sortOrder);
    return sorted;
  }

  if (sortKey === 'startAt') {
    // 시작 없음 = 태초부터 시작 → 맨 앞.
    const start = (r: T) => timeOf(r.displayStartAt) ?? Number.NEGATIVE_INFINITY;
    sorted.sort((a, b) => start(a) - start(b) || a.sortOrder - b.sortOrder);
    return sorted;
  }

  if (sortKey === 'endAt') {
    // 종료 없음 = 끝나지 않음 → 맨 뒤.
    const end = (r: T) => timeOf(r.displayEndAt) ?? Number.POSITIVE_INFINITY;
    sorted.sort((a, b) => end(a) - end(b) || a.sortOrder - b.sortOrder);
    return sorted;
  }

  const updated = (r: T) => timeOf(r.updatedAt) ?? Number.NEGATIVE_INFINITY;
  sorted.sort((a, b) => updated(b) - updated(a) || a.sortOrder - b.sortOrder);
  return sorted;
};

/**
 * 드래그로 바뀐 **한 페이지의 순서**를 전체 순서에 다시 끼워 넣는다.
 *
 * BE reorder 는 받은 배열의 인덱스를 sort_order 로 쓰고 누락된 id 는 건드리지 않으므로,
 * 페이지에 보이는 id 만 보내면 화면 밖 배너와 순서가 충돌한다. 페이지 구간만 교체한 **전체
 * 배열**을 만들어 보내면 페이징 중에도 드래그가 안전해진다(페이지 밖 항목은 자리를 지킨다).
 *
 * 반환값의 `sortOrder` 는 새 인덱스로 다시 매긴다 — BE 가 저장할 값과 같게 맞춰,
 * 서버 재조회 전까지의 낙관적 표시가 정렬에 의해 원위치로 튕기지 않게 한다.
 */
export const applyPageReorder = <T extends BannerListRow>(
  orderedAll: ReadonlyArray<T>,
  pageStartIndex: number,
  reorderedPage: ReadonlyArray<T>,
): T[] => {
  const next = orderedAll.slice();
  next.splice(pageStartIndex, reorderedPage.length, ...reorderedPage);
  return next.map((row, index) => ({ ...row, sortOrder: index }));
};

/** 한 건을 전체 순서의 맨 앞/맨 뒤로 보낸다. */
export type MoveEdge = 'top' | 'bottom';

/**
 * 페이지를 넘나드는 이동 수단. 드래그는 한 페이지 안에서만 가능하므로, 다른 페이지로 보내는
 * 가장 흔한 의도(맨 위로 올리기 / 맨 아래로 내리기)는 별도 동작으로 제공한다.
 *
 * 전체 순서 배열을 받아 해당 id 를 뽑아 끝으로 옮기고 `sortOrder` 를 다시 매긴 **전량**을
 * 돌려준다 — applyPageReorder 와 같은 이유로 부분집합을 보내면 안 된다.
 * id 가 없으면 입력을 그대로(복사해서) 돌려준다.
 */
export const moveRowToEdge = <T extends BannerListRow & { id: number }>(
  orderedAll: ReadonlyArray<T>,
  id: number,
  edge: MoveEdge,
): T[] => {
  const index = orderedAll.findIndex((r) => r.id === id);
  if (index < 0) return orderedAll.map((row, i) => ({ ...row, sortOrder: i }));
  const next = orderedAll.slice();
  const [moved] = next.splice(index, 1);
  if (!moved) return next.map((row, i) => ({ ...row, sortOrder: i }));
  if (edge === 'top') next.unshift(moved);
  else next.push(moved);
  return next.map((row, i) => ({ ...row, sortOrder: i }));
};

/**
 * 전체 순서에서 해당 배너가 몇 번째인지(사람이 읽는 **1-based** 순번). 없으면 null.
 *
 * 위치 지정 이동 다이얼로그가 "현재 몇 번째인지" 를 보여줄 때 쓴다. 화면의 행 번호를 그대로
 * 쓰면 안 된다 — 탭이 '노출 중' 이거나 페이징 중이면 화면 순번과 전체 순번이 다르다.
 */
export const positionOf = <T extends { id: number }>(
  orderedAll: ReadonlyArray<T>,
  id: number,
): number | null => {
  const index = orderedAll.findIndex((r) => r.id === id);
  return index < 0 ? null : index + 1;
};

/**
 * 한 건을 전체 순서의 **임의 위치**로 옮긴다. `맨 위로/맨 아래로` 의 일반화다 —
 * 50번째를 7번째로 보내는 것처럼 페이지를 몇 장 건너뛰는 이동은 드래그로도 edge 이동으로도
 * 불가능했다.
 *
 * `position` 은 사람이 보는 **1-based 순번**이고, 이동이 끝난 뒤 그 항목이 실제로 그 자리에
 * 오도록 계산한다(뽑아낸 뒤 `position - 1` 에 끼운다). 범위를 벗어나면 맨 앞/맨 뒤로 좁힌다 —
 * 입력 칸에서 막긴 하지만, 목록이 그 사이 줄어들 수 있으므로 여기서도 방어한다.
 *
 * moveRowToEdge 와 같은 이유로 **전량**을 돌려주고 `sortOrder` 를 다시 매긴다. 부분집합을
 * BE reorder 에 보내면 화면 밖 배너의 순서가 어긋난다.
 */
export const moveRowToPosition = <T extends BannerListRow & { id: number }>(
  orderedAll: ReadonlyArray<T>,
  id: number,
  position: number,
): T[] => {
  const renumber = (list: ReadonlyArray<T>): T[] => list.map((row, i) => ({ ...row, sortOrder: i }));
  const index = orderedAll.findIndex((r) => r.id === id);
  // 없는 id 나 숫자가 아닌 입력은 순서를 흔들지 않는다(재번호만 맞춘 사본).
  if (index < 0 || !Number.isFinite(position)) return renumber(orderedAll);
  const next = orderedAll.slice();
  const [moved] = next.splice(index, 1);
  if (!moved) return renumber(next);
  const target = Math.min(Math.max(Math.trunc(position), 1), orderedAll.length) - 1;
  next.splice(target, 0, moved);
  return renumber(next);
};
